// Prunes upstream's admin route tree to hide the surfaces enumerated in
// `route-blocklist`. Called once in `metabase/routes.tsx` around the
// `getAdminRoutes(...)` invocation — a single overlay hook that survives
// upstream route reshuffles because it matches on `path` strings, not
// line numbers.

import { Children, type ReactNode, cloneElement, isValidElement } from "react";

import { isBlockedAdminRoute } from "./route-blocklist";

// Joins a parent path with a child `path` prop, stripping leading slashes so
// blocklist entries like `admin/embedding` compose regardless of whether the
// upstream author wrote `path="/embedding"` or `path="embedding"`.
function joinPath(parent: string, child: string | undefined): string {
  if (!child) {
    return parent;
  }
  const cleanChild = child.replace(/^\/+/, "");
  if (!parent) {
    return cleanChild;
  }
  return `${parent}/${cleanChild}`;
}

function walk(node: ReactNode, parentPath: string): ReactNode {
  if (Array.isArray(node)) {
    return Children.map(node, (child) => walk(child, parentPath));
  }
  if (!isValidElement(node)) {
    return node;
  }

  const props = node.props as { path?: string; children?: ReactNode };
  const currentPath = joinPath(parentPath, props.path);

  if (props.path && isBlockedAdminRoute(currentPath)) {
    return null;
  }

  if (props.children === undefined) {
    return node;
  }

  return cloneElement(node, undefined, walk(props.children, currentPath));
}

export function filterIasBlockedRoutes(node: ReactNode): ReactNode {
  // The admin subtree comes in with `path="admin"` at the root, so we prime
  // the walk with an empty parent to let that node's own path establish the
  // prefix.
  return walk(node, "");
}
